import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter,
  getFirstCollision, pointerWithin, rectIntersection, useDroppable, useSensor, useSensors,
  type CollisionDetection, type DragEndEvent, type DragOverEvent, type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, errorText } from '../lib/api'
import { useCreateTask, useRefreshTasks } from '../lib/queries'
import { moveTaskInBoards } from '../lib/optimistic'
import { columnKey, type Status, type Task, type UserRef } from '../lib/types'
import { TaskCard } from './TaskCard'
import { Avatar, cx, useToast } from './ui'

type Columns = Record<string, Task[]>

function group(statuses: Status[], tasks: Task[], aggregated: boolean): Columns {
  const columns: Columns = {}
  for (const s of statuses) columns[columnKey(aggregated, s.id, s.name)] = []
  for (const t of tasks) {
    const key = columnKey(aggregated, t.statusId, t.statusName)
    ;(columns[key] ??= []).push(t)
  }
  return columns
}

export function Board({
  statuses, tasks, counts, projectId, aggregated, onOpenTask, pulsing, canEdit, onShowAllDone,
  selected, onToggleSelect, user, defaultAssignee,
}: {
  statuses: Status[]
  tasks: Task[]
  counts: Record<string, number>
  projectId: string
  /** Who is looking, so the create box can offer "Me". */
  user: UserRef
  /** Who this project's work goes to by default, if anybody. */
  defaultAssignee: UserRef | null
  /** Board spans sub-projects, so columns merge by name and cards show their project. */
  aggregated: boolean
  onOpenTask: (task: Task) => void
  pulsing: Record<string, number>
  canEdit: boolean
  onShowAllDone: () => void
  selected: Set<string>
  onToggleSelect: (id: string) => void
}) {
  const [columns, setColumns] = useState<Columns>(() => group(statuses, tasks, aggregated))
  const [activeTask, setActiveTask] = useState<Task | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toast = useToast()
  const refresh = useRefreshTasks()
  const queryClient = useQueryClient()

  // While a card is in the air, server data must not yank the columns out from
  // under it — the drag is the user's intent and it wins until it lands.
  const dragging = activeTask !== null
  const snapshot = useRef<Columns>(columns)
  useEffect(() => {
    if (!dragging) setColumns(group(statuses, tasks, aggregated))
  }, [statuses, tasks, aggregated, dragging])

  const move = useMutation({
    mutationFn: (v: {
      id: string
      target: { statusId?: string; statusName?: string }
      afterId: string | null
      beforeId: string | null
    }) => api.moveTask(v.id, v.target, v.afterId, v.beforeId),
    onError: (err) => {
      // Put the board back the way the server still believes it is.
      setColumns(snapshot.current)
      refresh(projectId)
      toast.show('That move did not stick', {
        tone: 'error',
        detail: errorText(err),
      })
    },
    // No refetch. The card is already where it was dropped, and the server
    // has just confirmed it — going back for the same board would only make
    // the column reflow a second time.
    onSuccess: (task) => {
      moveTaskInBoards(queryClient, task.id, {
        statusId: task.statusId, statusName: task.statusName, statusDone: task.statusDone,
      })
    },
  })

  const bulkMove = useMutation({
    mutationFn: (v: { taskIds: string[]; target: { statusId?: string; statusName?: string } }) =>
      api.bulkMove(v.taskIds, v.target),
    onError: (err) => {
      setColumns(snapshot.current)
      refresh(projectId)
      toast.show('That move did not stick', {
        tone: 'error',
        detail: errorText(err),
      })
    },
    onSuccess: (moved) => {
      for (const task of moved) {
        moveTaskInBoards(queryClient, task.id, {
          statusId: task.statusId, statusName: task.statusName, statusDone: task.statusDone,
        })
      }
      toast.show(`Moved ${moved.length} task${moved.length === 1 ? '' : 's'}`)
    },
  })

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so clicking a card to open
    // it does not turn into an accidental move.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  /**
   * Where a card would land.
   *
   * Pointer-first, because corner-distance strategies cannot see an empty
   * column: a column is hundreds of pixels tall so its corners are always
   * further from the dragged card than any small card's are, and the column
   * loses every comparison. That is why cards could never be dropped back into
   * an emptied column.
   */
  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      const hits = pointerWithin(args)
      const candidates = hits.length > 0 ? hits : rectIntersection(args)
      const overId = getFirstCollision(candidates, 'id')
      if (overId == null) return candidates

      // Over a column that holds cards: aim at the nearest card so the
      // insertion point is precise. An empty column stays the target itself.
      const inside = columns[String(overId)]
      if (inside && inside.length > 0) {
        const ids = new Set(inside.map((t) => t.id))
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((c) => ids.has(String(c.id))),
        })
      }
      return candidates
    },
    [columns],
  )

  const columnOf = useCallback(
    (id: string): string | null => {
      if (columns[id]) return id
      return Object.keys(columns).find((col) => columns[col]!.some((t) => t.id === id)) ?? null
    },
    [columns],
  )

  function onDragStart(e: DragStartEvent) {
    snapshot.current = columns
    const task = Object.values(columns).flat().find((t) => t.id === e.active.id)
    setActiveTask(task ?? null)
  }

  /** Moves the card between columns as it crosses, so the drop target is honest. */
  function onDragOver(e: DragOverEvent) {
    const { active, over } = e
    if (!over) return

    const from = columnOf(String(active.id))
    const to = columnOf(String(over.id))
    if (!from || !to || from === to) return

    setColumns((prev) => {
      const source = prev[from] ?? []
      const target = prev[to] ?? []
      const index = source.findIndex((t) => t.id === active.id)
      if (index < 0) return prev

      const moved = source[index]!
      const overIndex = target.findIndex((t) => t.id === over.id)
      const insertAt = overIndex >= 0 ? overIndex : target.length

      return {
        ...prev,
        [from]: source.filter((t) => t.id !== active.id),
        [to]: [...target.slice(0, insertAt), moved, ...target.slice(insertAt)],
      }
    })
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e
    setActiveTask(null)
    if (!over) {
      setColumns(snapshot.current)
      return
    }

    const target = columnOf(String(over.id))
    if (!target) {
      setColumns(snapshot.current)
      return
    }

    // Settle the final order inside the destination column.
    let finalColumns = columns
    const list = columns[target] ?? []
    const oldIndex = list.findIndex((t) => t.id === active.id)
    const newIndex = list.findIndex((t) => t.id === over.id)
    if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
      finalColumns = { ...columns, [target]: arrayMove(list, oldIndex, newIndex) }
      setColumns(finalColumns)
    }

    const settled = finalColumns[target] ?? []
    const at = settled.findIndex((t) => t.id === active.id)
    if (at < 0) {
      setColumns(snapshot.current)
      return
    }

    // Nothing actually moved: same column, same slot.
    const startedIn = columnOf(String(active.id))
    const wasAt = snapshot.current[target]?.findIndex((t) => t.id === active.id) ?? -1
    if (startedIn === target && wasAt === at) return

    const status = statuses.find((s) => columnKey(aggregated, s.id, s.name) === target)
    if (!status) return

    // Dragging one card of a selection moves the whole selection. The
    // selection survives the drop, so several columns can be worked through
    // without re-picking the same cards.
    const draggedId = String(active.id)
    if (selected.has(draggedId) && selected.size > 1) {
      const ids = [...selected]
      setColumns((prev) => {
        const moving = new Map<string, Task>()
        const next: Columns = {}
        for (const [key, list] of Object.entries(prev)) {
          next[key] = list.filter((t) => {
            if (!selected.has(t.id)) return true
            moving.set(t.id, t)
            return false
          })
        }
        // Appended in selection order, matching what the server does.
        next[target] = [
          ...(next[target] ?? []),
          ...ids.map((id) => moving.get(id)).filter((t): t is Task => Boolean(t)),
        ]
        return next
      })
      bulkMove.mutate({
        taskIds: ids,
        target: aggregated ? { statusName: status.name } : { statusId: status.id },
      })
      return
    }

    if (aggregated) {
      // Merged columns belong to no single project, so the destination is
      // named and the server resolves it inside the card's own project.
      // Neighbours are dropped: they may belong to other projects, whose
      // positions are on an unrelated scale.
      move.mutate({ id: String(active.id), target: { statusName: status.name }, afterId: null, beforeId: null })
      return
    }

    // Neighbours, not an index: the server resolves real positions at write
    // time, so a slightly stale column on screen still lands the card right.
    move.mutate({
      id: String(active.id),
      target: { statusId: status.id },
      afterId: at > 0 ? settled[at - 1]!.id : null,
      beforeId: at < settled.length - 1 ? settled[at + 1]!.id : null,
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActiveTask(null)
        setColumns(snapshot.current)
      }}
    >
      <div className="flex h-full items-start gap-3 overflow-x-auto overflow-y-hidden px-4 pb-4">
        {statuses.map((status) => {
          const key = columnKey(aggregated, status.id, status.name)
          return (
            <Column
              key={key}
              columnId={key}
              status={status}
              tasks={columns[key] ?? []}
              total={counts[key] ?? (columns[key]?.length ?? 0)}
              projectId={projectId}
              aggregated={aggregated}
              user={user}
              defaultAssignee={defaultAssignee}
              collapsed={Boolean(collapsed[key])}
              onToggleCollapse={() => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))}
              onShowAllDone={onShowAllDone}
              onOpenTask={onOpenTask}
              pulsing={pulsing}
              canEdit={canEdit}
              selected={selected}
              onToggleSelect={onToggleSelect}
            />
          )
        })}
      </div>

      <DragOverlay dropAnimation={{ duration: 160, easing: 'cubic-bezier(0.16,0.8,0.32,1)' }}>
        {activeTask && (
          <div className="relative w-[308px]">
            {/* Dragging a selection carries all of it, so the overlay says how
                many rather than pretending it is one card. */}
            {selected.has(activeTask.id) && selected.size > 1 && (
              <>
                <div className="absolute inset-x-2 -bottom-1.5 h-full rounded-[7px] border border-[var(--line-strong)] bg-[var(--surface)] opacity-60" />
                <div className="absolute inset-x-1 -bottom-0.5 h-full rounded-[7px] border border-[var(--line-strong)] bg-[var(--surface)] opacity-80" />
                <span className="absolute -right-2 -top-2 z-10 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[11px] font-semibold text-white">
                  {selected.size}
                </span>
              </>
            )}
            <div className="relative">
              <TaskCard task={activeTask} onOpen={() => {}} dragging showProject={aggregated} />
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

function Column({
  columnId, status, tasks, total, projectId, aggregated, user, defaultAssignee,
  collapsed, onToggleCollapse,
  onShowAllDone, onOpenTask, pulsing, canEdit, selected, onToggleSelect,
}: {
  columnId: string
  status: Status
  tasks: Task[]
  total: number
  projectId: string
  aggregated: boolean
  user: UserRef
  defaultAssignee: UserRef | null
  collapsed: boolean
  onToggleCollapse: () => void
  onShowAllDone: () => void
  onOpenTask: (task: Task) => void
  pulsing: Record<string, number>
  canEdit: boolean
  selected: Set<string>
  onToggleSelect: (id: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId })
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  // Who the next card is for. Starts at the project's answer, and survives
  // between cards because adding several in a row is the common case.
  const [assignTo, setAssignTo] = useState<string | null>(defaultAssignee?.id ?? null)
  useEffect(() => setAssignTo(defaultAssignee?.id ?? null), [defaultAssignee?.id])
  const create = useCreateTask()
  const toast = useToast()

  // The server caps done columns because a board with four hundred completed
  // cards is unusable. `total` is the real number, so the column can say how
  // many it is holding back instead of silently losing them.
  const hidden = Math.max(0, total - tasks.length)

  async function submit() {
    const value = title.trim()
    if (!value) {
      setAdding(false)
      return
    }
    try {
      // On a merged board the column belongs to several projects, so the new
      // task names it instead: the server resolves that name inside the
      // project whose board is open.
      await create.mutateAsync({
        projectId,
        title: value,
        // Always explicit: an omitted assignee means "use the project
        // default", and picking Nobody has to be able to override that.
        assigneeId: assignTo,
        ...(aggregated ? { statusName: status.name } : { statusId: status.id }),
      })
      setTitle('') // stay open: adding several in a row is the common case
    } catch (err) {
      toast.show('Could not create that task', {
        tone: 'error',
        detail: errorText(err),
      })
    }
  }

  if (collapsed) {
    return (
      <div className="flex h-full w-10 shrink-0 flex-col items-center rounded-lg border border-[var(--line)] bg-[var(--sunken)] py-2">
        <button
          onClick={onToggleCollapse}
          className="flex flex-1 flex-col items-center gap-2 text-[var(--ink-2)] hover:text-[var(--ink)]"
          aria-label={`Expand ${status.name}`}
        >
          <span className="task-key text-[12px]">{total}</span>
          <span className="[writing-mode:vertical-rl] text-[13px] font-medium">{status.name}</span>
        </button>
      </div>
    )
  }

  return (
    <section className="flex h-full w-[324px] shrink-0 flex-col">
      <header className="flex items-center gap-2 px-1 pb-2">
        {/* The status colour appears here and nowhere else — one cap per
            column instead of colour on every card. */}
        <span
          aria-hidden
          className="h-[3px] w-4 shrink-0 rounded-full"
          style={{ background: status.color }}
        />
        <h2 className="text-[13.5px] font-semibold tracking-tight">{status.name}</h2>
        <span className="task-key text-[12px] text-[var(--ink-3)]">{total}</span>

        <button
          onClick={onToggleCollapse}
          aria-label={`Collapse ${status.name}`}
          className="ml-auto rounded p-1 text-[var(--ink-3)] opacity-0 transition-opacity duration-[140ms] hover:bg-[var(--sunken)] hover:text-[var(--ink)] focus-visible:opacity-100 [section:hover_&]:opacity-100"
        >
          <CollapseGlyph />
        </button>
      </header>

      <div
        ref={setNodeRef}
        className={cx(
          'flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-lg border p-2',
          'transition-colors duration-[140ms]',
          isOver
            ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
            : 'border-transparent bg-[var(--sunken)]',
        )}
      >
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onOpen={onOpenTask}
              pulsing={Boolean(pulsing[task.id])}
              showProject={aggregated}
              selected={selected.has(task.id)}
              selectionActive={selected.size > 0}
              onToggleSelect={() => onToggleSelect(task.id)}
            />
          ))}
        </SortableContext>

        {hidden > 0 && (
          <button
            onClick={onShowAllDone}
            className="rounded-md py-1.5 text-[13px] text-[var(--ink-3)] transition-colors duration-[140ms] hover:bg-[var(--surface)] hover:text-[var(--ink-2)]"
          >
            Show {hidden} older
          </button>
        )}

        {/* The add box sits at the end of the column and new cards append, so
            what you type appears where you typed it. */}
        {adding ? (
          <div className="rounded-[7px] border border-[var(--accent)] bg-[var(--surface)]">
            <textarea
              autoFocus
              rows={2}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={submit}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  submit()
                } else if (e.key === 'Escape') {
                  setTitle('')
                  setAdding(false)
                }
              }}
              placeholder="What needs doing?"
              className={cx(
                'w-full resize-none bg-transparent',
                'px-3 py-2.5 text-[14.5px] leading-snug outline-none placeholder:text-[var(--ink-3)]',
              )}
            />
            <AssignChoice
              value={assignTo}
              onChange={setAssignTo}
              user={user}
              defaultAssignee={defaultAssignee}
            />
          </div>
        ) : canEdit ? (
          <button
            onClick={() => setAdding(true)}
            className={cx(
              'rounded-md border border-dashed border-[var(--line-strong)] text-[12px]',
              'text-[var(--ink-3)] transition-colors duration-[140ms]',
              'hover:border-[var(--accent)] hover:text-[var(--ink-2)]',
              tasks.length === 0 ? 'py-5' : 'py-2',
            )}
          >
            + Add a task
          </button>
        ) : (
          tasks.length === 0 && (
            <p className="py-5 text-center text-[12px] text-[var(--ink-3)]">Nothing here</p>
          )
        )}
      </div>
    </section>
  )
}

/**
 * Who the card being typed is for.
 *
 * It sits under the title box rather than behind a menu because the decision
 * is made at the same moment as the title, and a menu turns a one-word answer
 * into three clicks. Most of the time the project already knows the answer and
 * this is just confirming it, so the whole control is one row of faces.
 *
 * Clicking here must not blur the textarea — that would submit a half-typed
 * title — so the buttons refuse focus on mousedown.
 */
function AssignChoice({
  value, onChange, user, defaultAssignee,
}: {
  value: string | null
  onChange: (id: string | null) => void
  user: UserRef
  defaultAssignee: UserRef | null
}) {
  // When the project's person is you, "Marat" and "Me" are the same button.
  const responsible = defaultAssignee && defaultAssignee.id !== user.id ? defaultAssignee : null

  const options: Array<{ id: string | null; label: string; who: UserRef | null }> = [
    ...(responsible ? [{ id: responsible.id, label: firstName(responsible.name), who: responsible }] : []),
    { id: user.id, label: 'Me', who: user },
    { id: null, label: 'Nobody', who: null },
  ]

  return (
    <div
      role="radiogroup"
      aria-label="Who this task is for"
      className="flex flex-wrap items-center gap-1 border-t border-[var(--line)] px-2 py-1.5"
    >
      {options.map((o) => {
        const on = value === o.id
        return (
          <button
            key={o.id ?? 'nobody'}
            role="radio"
            aria-checked={on}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(o.id)}
            className={cx(
              'flex items-center gap-1 rounded-full border py-0.5 pr-2 text-[11.5px]',
              'transition-colors duration-[140ms]',
              o.who ? 'pl-0.5' : 'pl-2',
              on
                ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                : 'border-transparent text-[var(--ink-3)] hover:border-[var(--line)] hover:text-[var(--ink-2)]',
            )}
          >
            {o.who && <Avatar user={o.who} size={16} />}
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name
}

function CollapseGlyph() {
  return (
    <svg
      width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden
    >
      <path d="M9.5 4 5.5 8l4 4" />
    </svg>
  )
}
